import ArrowRightSmallUpIcon from '@/components/icons/ArrowRightSmallUpIcon'
import { OPERATIONS_ROUTES } from '@/constant/route'
import useNotifications from '@/features/operations/hooks/notification/useNotifications'
import { useAuthContext } from '@/features/auth/context'
import useUser from '@/features/auth/hooks/useUser'
import { AlertResponse } from '@/types/notification'
import { AlertType, NotificationSearchParams } from '@/types/notification'
import { TabContext, TabPanel } from '@mui/lab'
import { CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material'
import { User } from '@sentry/react'
import { useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Link, useLocation } from 'react-router-dom'
import ListAlerts from './ListAlerts'
import classes from './styles.module.scss'
import useTranslation from '@/hooks/useTranslation'

interface NotificationAlertsModalProps {
  interfaceShowing?: number | null
}

const NotificationAlertsModal = ({
  interfaceShowing,
}: NotificationAlertsModalProps) => {
  const { t } = useTranslation()

  const { data: user } = useUser<User>()
  const { interfaceId: authInterfaceId } = useAuthContext()
  // Allow a caller (e.g. MapView showing a non-default interface) to scope
  // notifications to a specific interface; fall back to the selected one.
  const interfaceId = interfaceShowing || authInterfaceId

  const [tab, setTab] = useState<string>('0')

  const handleChangeTab = (_: any, newTab: string) => {
    setTab(newTab)
  }

  const [searchParams, setSearchParams] = useState<NotificationSearchParams>({
    PageIndex: 1,
    PageSize: 10,
    SortDirection: 'desc',
  })

  const { data } = useNotifications(String(interfaceId), searchParams)

  const [listAlerts, setListAlerts] = useState<AlertResponse[]>([])

  useEffect(() => {
    setSearchParams({
      ...searchParams,
      Types:
        tab === '1'
          ? [AlertType.Normal]
          : tab === '2'
          ? [AlertType.Info]
          : undefined,
    })
  }, [tab])

  useEffect(() => {
    if (data?.list) {
      setListAlerts(data.list)
    }
  }, [data])

  const loadMore = () => {
    // Calculate the new PageSize (increase by 10) each time you load more data
    setSearchParams((prevSearchParams) => ({
      ...prevSearchParams,
      PageSize: (prevSearchParams?.PageSize || 0) + 10, // Use a default value of 0 if prevSearchParams is undefined
    }))
  }

  const location = useLocation()

  // Kiểm tra xem đang ở route nào
  const isAlertsRoute = location.pathname === OPERATIONS_ROUTES.alerts.to

  return (
    <Stack className={classes.root}>
      <Stack className={classes.section_alerts}>
        <Typography className={classes.txtAlerts}>
          {t('notifications.titleAlerts')}
        </Typography>
      </Stack>
      <Stack className={classes.section_body}>
        <Stack
          direction="row"
          justifyContent="space-between"
          p="16px 20px 0px 20px"
        >
          <TabContext value={tab}>
            <Tabs
              value={tab}
              onChange={handleChangeTab}
              textColor="inherit"
              sx={{
                minHeight: '38px',
                background: 'transparent',
                width: '292px',
                '.MuiTab-root': {
                  flex: 1,
                  height: '38px',
                  minHeight: '38px',
                  fontWeight: 400,
                  lineHeight: '115%',
                  fontSize: '16px',
                  color: '#8A8A8A',
                  '&.Mui-selected': {
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255, 255, 255, 0.10)',
                    fontSize: '16px',
                    fontWeight: '700',
                    borderRadius: '4px',
                  },
                },
                '.MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              <Tab value={'0'} label={t('notifications.tabs.all')} />
              <Tab value={'1'} label={t('notifications.tabs.error')} />
              <Tab value={'2'} label={t('notifications.tabs.info')} />
            </Tabs>
          </TabContext>
          {!isAlertsRoute && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ textDecoration: 'none' }}
              component={Link}
              to={OPERATIONS_ROUTES.alerts.to}
            >
              <Typography className={classes.txtViewAll}>
                {t('viewAll')}
              </Typography>
              <ArrowRightSmallUpIcon sx={{ color: '#fff' }} />
            </Stack>
          )}
        </Stack>
        <TabContext value={tab}>
          <Stack flexGrow={1}>
            {tab === '0' && (
              <TabPanel value={'0'} className={classes.tabAll}>
                <InfiniteScroll
                  className={classes.infiniteScroll}
                  dataLength={listAlerts.length || 0}
                  next={loadMore}
                  hasMore={
                    data ? (data.list?.length || 0) < data.totalCount : false
                  }
                  loader={
                    <Stack key={0} justifyContent="center" alignItems="center">
                      <CircularProgress sx={{ color: 'white' }} />
                    </Stack>
                  }
                  height={420}
                  endMessage={
                    <Stack style={{ textAlign: 'center' }}>
                      <Typography className={classes.txtSeeAll}>
                        {t('notifications.noAlerts')}
                      </Typography>
                    </Stack>
                  }
                >
                  {listAlerts && <ListAlerts rows={listAlerts} />}
                </InfiniteScroll>
              </TabPanel>
            )}
            {tab === '1' && (
              <TabPanel value={'1'} className={classes.tabAll}>
                <InfiniteScroll
                  className={classes.infiniteScroll}
                  dataLength={listAlerts.length || 0}
                  next={loadMore}
                  hasMore={
                    data ? (data.list?.length || 0) < data.totalCount : false
                  }
                  loader={
                    <Stack key={0} justifyContent="center" alignItems="center">
                      <CircularProgress sx={{ color: 'white' }} />
                    </Stack>
                  }
                  height={410}
                  endMessage={
                    <Stack style={{ textAlign: 'center' }}>
                      <Typography className={classes.txtSeeAll}>
                        {t('notifications.noAlerts')}
                      </Typography>
                    </Stack>
                  }
                >
                  {listAlerts && <ListAlerts rows={listAlerts} />}
                </InfiniteScroll>
              </TabPanel>
            )}
            {tab === '2' && (
              <TabPanel value={'2'} className={classes.tabAll}>
                <InfiniteScroll
                  className={classes.infiniteScroll}
                  dataLength={listAlerts.length || 0}
                  next={loadMore}
                  hasMore={
                    data ? (data.list?.length || 0) < data.totalCount : false
                  }
                  loader={
                    <Stack key={0} justifyContent="center" alignItems="center">
                      <CircularProgress sx={{ color: 'white' }} />
                    </Stack>
                  }
                  height={410}
                  endMessage={
                    <Stack style={{ textAlign: 'center' }}>
                      <Typography className={classes.txtSeeAll}>
                        {t('notifications.noAlerts')}
                      </Typography>
                    </Stack>
                  }
                >
                  {listAlerts && <ListAlerts rows={listAlerts} />}
                </InfiniteScroll>
              </TabPanel>
            )}
          </Stack>
        </TabContext>
      </Stack>
    </Stack>
  )
}

export default NotificationAlertsModal
